import { useTranslation } from 'react-i18next';
import type { RunRecord, PipelineStage } from '../types';

interface Props {
  run: RunRecord;
}

const STAGE_ORDER = [
  'search_specialist',
  'refine_search',
  'data_analyst',
  'architecture_planner',
  'review_writer',
  'citation_agent',
] as const;

const STAGE_META: Record<string, { icon: string; labelCn: string }> = {
  search_specialist: { icon: '◎', labelCn: '搜索' },
  refine_search: { icon: '◇', labelCn: '精化' },
  data_analyst: { icon: '◌', labelCn: '分析' },
  architecture_planner: { icon: '⌬', labelCn: '架构' },
  review_writer: { icon: '✦', labelCn: '写作' },
  citation_agent: { icon: '▣', labelCn: '引文' },
};

const STATUS_COLOR: Record<string, string> = {
  queued: '#94a3b8',
  running: '#3b82f6',
  completed: '#22c55e',
  failed: '#ef4444',
  interrupted: '#f59e0b',
};

function inferStageFromProgress(progress: number): { index: number; percent: number } {
  const clamped = Math.max(0, Math.min(99, progress));
  const index = Math.min(STAGE_ORDER.length - 1, Math.floor(clamped / (100 / STAGE_ORDER.length)));
  const stageStart = index * (100 / STAGE_ORDER.length);
  const percent = Math.max(1, Math.min(99, ((clamped - stageStart) / (100 / STAGE_ORDER.length)) * 100));
  return { index, percent };
}

function shouldInferStages(stages: PipelineStage[]): boolean {
  return stages.length === 0 || stages.every((stage) => (
    stage.status === 'pending'
    && Math.round(stage.percent || 0) === 0
    && (!stage.message || stage.message === 'Waiting to start')
  ));
}

function getStatusTone(status: string): string {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'running') return 'info';
  if (status === 'interrupted') return 'warning';
  return 'muted';
}

export default function ProgressPanel({ run }: Props) {
  const { t } = useTranslation();

  const inferFromRun = shouldInferStages(run.stages);
  const inferred = inferStageFromProgress(run.progress);
  const currentStageIndex = run.currentStage && STAGE_ORDER.includes(run.currentStage as typeof STAGE_ORDER[number])
    ? STAGE_ORDER.indexOf(run.currentStage as typeof STAGE_ORDER[number])
    : inferred.index;
  const stageMap = new Map(run.stages.map((stage) => [stage.key, stage]));
  const orderedStages: (PipelineStage & { icon: string; labelCn: string })[] = STAGE_ORDER.map((key, index) => {
    const stage = stageMap.get(key);
    const meta = STAGE_META[key] || { icon: '•', labelCn: key };
    const inferredStatus = index < currentStageIndex ? 'completed' : index === currentStageIndex ? 'running' : 'pending';
    const inferredPercent = index < currentStageIndex ? 100 : index === currentStageIndex ? inferred.percent : 0;
    const staleMessage = !stage?.message || stage.message === 'Waiting to start';

    return {
      key,
      label: stage?.label || key,
      labelCn: meta.labelCn,
      status: inferFromRun ? inferredStatus : stage?.status || inferredStatus,
      percent: inferFromRun ? inferredPercent : stage?.percent || (stage?.status === 'running' ? inferredPercent : 0),
      message: inferFromRun && index === currentStageIndex ? run.message : staleMessage && index === currentStageIndex ? run.message : stage?.message || '',
      startedAt: stage?.startedAt || null,
      metrics: stage?.metrics || {},
      icon: meta.icon,
    };
  });

  const activeStage = orderedStages.find((stage) => stage.status === 'running')
    || orderedStages.find((stage) => stage.status === 'completed' && stage.percent === 100)
    || orderedStages[0];

  const primaryError = run.errors[0]?.message || (run.status === 'failed' ? run.message : '');
  const completedStages = orderedStages.filter((stage) => stage.status === 'completed').length;

  return (
    <section className="progress-dock">
      <div className="progress-dock-header">
        <div className="progress-dock-copy">
          <p className="progress-kicker">Live Pipeline</p>
          <div className="progress-title-row">
            <h2 className="progress-title">{run.topic || 'Untitled Review Task'}</h2>
            <span className={`status-badge status-badge-panel tone-${getStatusTone(run.status)}`} style={{ background: STATUS_COLOR[run.status] || '#64748b' }}>
              {t(`status.${run.status}`, run.status)}
            </span>
          </div>
          <p className="progress-subtitle">{primaryError || activeStage?.message || run.message || '任务正在处理多阶段综述流程。'}</p>
        </div>
        <div className="progress-run-meta">
          <span className="progress-run-label">Run</span>
          <span className="run-id">#{run.runId.slice(-8)}</span>
        </div>
      </div>

      <div className="progress-overview-grid">
        <div className="progress-overview-card progress-overview-primary">
          <div className="progress-overview-topline">
            <span className="progress-overview-label">{t('run.progress')}</span>
            <span className="progress-overview-value mono">{Math.round(run.progress)}%</span>
          </div>
          <div className="progress-bar-track progress-bar-track-panel">
            <div className="progress-bar-fill progress-bar-fill-panel" style={{ width: `${run.progress}%` }} />
          </div>
          <div className="progress-footnote">
            <span>{completedStages}/{orderedStages.length} {t('run.stages')}</span>
            <span>{activeStage?.labelCn || activeStage?.label || run.currentStage}</span>
          </div>
        </div>

        <div className="progress-overview-card">
          <span className="progress-overview-label">{t('run.status')}</span>
          <strong className="progress-stage-name">{activeStage?.labelCn || activeStage?.label || '—'}</strong>
          <p className="progress-card-copy">{primaryError || activeStage?.message || run.message || '等待阶段状态更新。'}</p>
        </div>
      </div>

      <div className="progress-stage-rail">
        {orderedStages.map((stage, index) => {
          const isActive = stage.status === 'running';
          const isDone = stage.status === 'completed';
          const isFailed = stage.status === 'failed';
          return (
            <div
              key={stage.key}
              className={`stage-rail-item ${isActive ? 'active' : ''} ${isDone ? 'done' : ''} ${isFailed ? 'failed' : ''}`}
            >
              <div className="stage-rail-node-wrap">
                <div className="stage-rail-node">{stage.icon}</div>
                {index < orderedStages.length - 1 && <div className="stage-rail-line" />}
              </div>
              <div className="stage-rail-content">
                <div className="stage-rail-heading">
                  <span className="stage-rail-label">{stage.labelCn}</span>
                  <span className="stage-rail-percent mono">{Math.round(stage.percent)}%</span>
                </div>
                <p className="stage-rail-message">{stage.message || stage.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {(run.errors.length > 0 || primaryError) && (
        <div className="progress-alert-stack">
          {primaryError && run.errors.length === 0 && (
            <div className="progress-alert-card">
              <span className="progress-alert-code">[FAILED]</span>
              <span className="progress-alert-text">{primaryError}</span>
            </div>
          )}
          {run.errors.slice(0, 3).map((error, index) => (
            <div key={index} className="progress-alert-card">
              <span className="progress-alert-code">[{error.code}]</span>
              <span className="progress-alert-text">{error.message}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
